import { Injectable } from '@angular/core';
import { IChatConversation, IChatConversationMessage, IChatSuggestion } from '@fi-shared/fi-chat/fi-chat';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { ILightWeightUser, ILightWeightUserWithUnits } from '@fi-shared/models';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiChatConversationType } from '@fi-shared/fi-chat/fi-chat-conversation-type';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { ActionType, ObjectTypes, PersonalNotificationsCategoryType } from '@fi-shared/types';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { isDefined } from '@fi-shared/utils';
import { FiNotificationHelperService } from '@fi-shared/fi-notification/fi-notification-helper.service';
import { filter, take } from 'rxjs/operators';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { AppTextService } from '@fi-shared/app-text';
import { isNullOrUndefined } from 'util';
import { FrontendMode, MobileAppHelperBaseService } from '@fi-shared/fi-shared-services/mobile-app-helper-base.service';
import { FiChatSyncService } from '@fi-shared/fi-chat/fi-chat-sync.service';
import { getClientGuid } from '@fi-shared/utils/client-guid';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { FiDebugInfoService } from '@fi-shared/fi-shared-services/fi-debug-info.service';
import { FiAppSettings } from '@fi-shared/fi-app-settings';

@Injectable()
export class FiChatService {
    private _resetSendMessageProcess$ = new Subject<void>();
    get resetSendMessageProcess$() {
        return this._resetSendMessageProcess$.asObservable();
    }

    private _suggestedConversations$ = new BehaviorSubject<IChatSuggestion[]>([]);
    get suggestedConversations$() {
        return this._suggestedConversations$.asObservable();
    }

    private isSuggestionLoaded = false;

    //region constructor & synchronization
    constructor(
        private fiBackend: FiBackendService,
        private userDataService: FiUserDataService,
        private fiLoading: LoadingService,
        private fiSignalRService: FiSignalRService,
        private fiSignalRHelperService: FiSignalRHelperService,
        private fiChatHelperService: FiChatHelperService,
        private fiChatSyncService: FiChatSyncService,
        private fiNotificationHelperService: FiNotificationHelperService,
        private mobileAppHelperService: MobileAppHelperBaseService,
        private snackBar: FiSnackBarService,
        private appTextService: AppTextService,
        private debugInfoService: FiDebugInfoService,
    ) {
        this.fiSignalRHelperService.initialConversations$.subscribe((conversations) => {
            this.isSuggestionLoaded = false;
            this.fiChatSyncService.triggerConversationsUpdated({
                updatedConversations: conversations,
            });
        });

        this.fiChatHelperService.onConversationListOpening$.subscribe(() => {
            if (!this.isSuggestionLoaded) {
                this.isSuggestionLoaded = true;
                this.reloadSuggestions();
            }
        });

        // synchronization
        this.fiSignalRHelperService.startUpdateItemsEventProcessing(
            ObjectTypes.ChatConversationMessage,
            () => [],
            (conversationMessageIdsToFetch) => {
                return this.getConversationMessagesByIds(conversationMessageIdsToFetch);
            },
            (updatedConversationMessages: IChatConversationMessage[]) => {
                updatedConversationMessages.forEach((conversationMessage) =>
                    this.fiChatSyncService.triggerConversationMessageUpdated(conversationMessage),
                );
            },
            0,
        );

        this.fiSignalRHelperService.startUpdateItemsEventProcessing(
            ObjectTypes.ChatConversation,
            () => this.fiChatSyncService.conversations,
            (conversationIdsToFetch) => this.getConversations({ conversationIds: conversationIdsToFetch }),
            (updatedConversations) => {
                const newConversationIds = updatedConversations.map((item) => item.Id);
                this.fiChatSyncService.triggerConversationsUpdated({
                    updatedConversations: updatedConversations,
                    $event: {
                        changedConversationIds: newConversationIds,
                    },
                });
            },
            0,
        );

        this.startConversationsChangesTimer();
    }

    subscribeToSignalREvents() {
        this.fiSignalRService.chatConversationMessageUpdatedEvent.subscribe((data) => {
            this.debugInfoService.addInfo('signalR message updated ' + JSON.stringify(data));
            data.Data.forEach((item) => {
                if (
                    data.ClientGuid !== getClientGuid() ||
                    this.fiChatHelperService.isSystemMessage(item.ConversationMessageType)
                )
                    this.fiSignalRHelperService.raiseUpdateItemsEvent(
                        item.ConversationMessageId,
                        ObjectTypes.ChatConversationMessage,
                        ActionType.Update,
                    );
            });
        });
    }

    conversationsChangesTimer: NodeJS.Timeout;
    checkConversationsChanges() {
        this.stopConversationsChangesTimer();
        if (!this.fiChatHelperService.hasAccessToChat()) {
            return;
        }

        const promise = this.getConversations({ supressErrorMessage: true });
        promise
            .then((newConversations) => {
                const currentConversations = this.fiChatSyncService.conversations;
                const changedConversationIds: number[] = [];
                newConversations.forEach((newConversation) => {
                    const existingConversation = currentConversations.find((c) => c.Id === newConversation.Id);
                    if (existingConversation) {
                        const currentLatestActivity = existingConversation.LatestActivityMessage?.ChangedOn ?? '';
                        // latest activity received from backend
                        const newLatestActivity = newConversation.LatestActivityMessage?.ChangedOn ?? '';
                        if (currentLatestActivity !== newLatestActivity) {
                            // we have chat conversation out of sync with the server so have to force reload data
                            changedConversationIds.push(newConversation.Id);
                        }
                    }
                });
                this.fiChatSyncService.triggerConversationsUpdated({
                    updatedConversations: newConversations,
                    $event: {
                        changedConversationIds: changedConversationIds,
                        forceReload: true,
                    },
                });
            })
            .finally(() => {
                this.startConversationsChangesTimer();
            });
    }

    startConversationsChangesTimer() {
        this.stopConversationsChangesTimer();
        this.conversationsChangesTimer = setTimeout(() => {
            this.checkConversationsChanges();
        }, FiAppSettings.CheckConversationsChangesInterval);
    }
    private stopConversationsChangesTimer() {
        if (this.conversationsChangesTimer) {
            clearTimeout(this.conversationsChangesTimer);
            this.conversationsChangesTimer = undefined;
        }
    }
    //endregion

    private getConversations(args?: { conversationIds?: number[]; supressErrorMessage?: boolean }) {
        return this.fiBackend.fetchDataPost<IChatConversation[]>('Chat/Chat/GetConversations', args?.conversationIds, {
            supressErrorMessage: args?.supressErrorMessage,
        });
    }

    getAvailableUsersForConversation(userIds?: number[]) {
        return this.fiBackend.fetchDataPost<ILightWeightUserWithUnits[]>(
            'Chat/Chat/GetAvailableUsersForConversation',
            userIds,
        );
    }

    private reloadSuggestions() {
        this.fiBackend
            .fetchData<IChatSuggestion[]>('Chat/Chat/GetSuggestions')
            .then((suggestions) => this._suggestedConversations$.next(suggestions));
    }

    findExistingConversations(selectedUserIds: number[], type: FiChatConversationType) {
        // obsolete, for flutter app testing
        return this.fiBackend.fetchDataPost<IChatConversation[]>('Chat/Chat/FindExistingConversations', {
            Type: type,
            SelectedUserIds: selectedUserIds,
        });
    }

    createNewConversation(selectedUserIds: number[], type: FiChatConversationType) {
        const promise = this.fiBackend.fetchDataPost<IChatConversation>('Chat/Chat/CreateNewConversation', {
            Type: type,
            SelectedUserIds: selectedUserIds,
        });
        promise.then((newConversation) => {
            this.fiChatSyncService.triggerConversationsUpdated({
                updatedConversations: [newConversation].concat(this.fiChatSyncService.conversations),
            });
        });
        this.reloadSuggestions();
        return this.fiLoading.trackPromise(promise);
    }

    getConversationMessages(conversationId: number, offset: number, numberOfRecordsToTake: number) {
        return this.fiBackend.fetchData<IChatConversationMessage[]>(
            `Chat/Chat/GetConversationMessages/${conversationId}/${offset}/${numberOfRecordsToTake}`,
        );
    }

    private getConversationMessagesByIds(conversationMessageIds: number[]) {
        return this.fiBackend.fetchDataPost<IChatConversationMessage[]>(
            'Chat/Chat/GetConversationMessagesByIds/',
            conversationMessageIds,
        );
    }

    saveConversationMessage(conversationId: number, message: IChatConversationMessage) {
        const promise = this.fiBackend.fetchDataPost<number>(
            'Chat/Chat/SaveConversationMessage/' + conversationId,
            message,
        );
        promise.then((messageId) => {
            const now = new Date().toISOString();
            const currentUser = this.userDataService.getUserData();
            if (!message.Id) {
                message.CreatedByUserId = currentUser.UserId;
                message.CreatedByUserName = currentUser.FullName;
                message.CreatedOn = now;
            }
            message.ChangedByUserId = currentUser.UserId;
            message.ChangedByUserName = currentUser.FullName;
            message.ChangedOn = now;
            message.Id = messageId;
            message.ConversationId = conversationId;
            this.fiChatSyncService.triggerConversationMessageUpdated(message);
            this._resetSendMessageProcess$.next();
        });
        return this.fiLoading.trackPromise(promise);
    }

    markConversationAsVisited(conversationId: number) {
        const promise = this.fiBackend.fetchDataPost<number[]>('Chat/Chat/MarkConversationAsVisited/' + conversationId);
        promise.then(() => {
            this.fiChatSyncService.updateConversation(conversationId, (currentConversation) => {
                currentConversation.NotificationCategory = PersonalNotificationsCategoryType.Visited;
            });
        });
        return promise;
    }

    markAllConversationsAsRead(): Promise<number[]> {
        const newConversationIds = this.fiChatSyncService.newConversations.map((conversation) => conversation.Id);
        if (!newConversationIds.length) return Promise.resolve([]);
        const promise = this.fiBackend.fetchDataPost<number[]>('Chat/Chat/MarkConversationsAsRead', newConversationIds);
        promise.then(() => {
            newConversationIds.forEach((conversationId) => {
                this.fiChatSyncService.updateConversation(conversationId, (currentConversation) => {
                    this.fiNotificationHelperService.setIsSeen(currentConversation);
                });
            });
        });
        return promise;
    }

    deleteConversationMessageForUser(conversationId: number, messageId: number) {
        const promise = this.fiBackend.fetchDataPost<number>(
            'Chat/Chat/UpdateConversationMessageIsHidden/' + messageId,
            { isHidden: true },
        );
        promise.then(() => {
            // for proper updating conversation messages
            this.fiChatSyncService.updateConversation(conversationId, (conversation) => {});
        });
        this.reloadSuggestions();
        return this.fiLoading.trackPromise(promise);
    }

    deleteConversationMessages(conversationId: number, conversationMessageIds: number[]) {
        const promise = this.fiBackend.fetchDataPost<number>(
            'Chat/Chat/DeleteConversationMessages/' + conversationId,
            conversationMessageIds,
        );
        promise.then(() => {
            // for proper updating conversation messages
            this.fiChatSyncService.updateConversation(conversationId, (conversation) => {});
        });
        this.reloadSuggestions();
        return this.fiLoading.trackPromise(promise);
    }

    updateConversationIsHidden(conversationId: number, newIsHidden: boolean) {
        const promise = this.fiBackend.fetchDataPost<number>(
            'Chat/Chat/UpdateConversationIsHidden/' + conversationId,
            newIsHidden,
        );
        promise.then(() => {
            this.fiChatSyncService.updateConversation(conversationId, (currentConversation) => {
                currentConversation.IsHidden = newIsHidden;
                this.reloadSuggestions();
            }); // for proper updating conversations list
        });
        return this.fiLoading.trackPromise(promise);
    }
    changeConversationName(conversationId: number, newName: string) {
        const promise = this.fiBackend.fetchDataPost<void>('Chat/Chat/ChangeConversationNameV2/' + conversationId, {
            newName: newName,
        });
        promise.then(() => {
            // for proper updating conversation name
            this.fiChatSyncService.updateConversation(conversationId, (conversation) => {
                conversation.Name = newName;
            });
        });
        return this.fiLoading.trackPromise(promise);
    }

    leaveConversation(conversationId: number) {
        const promise = this.fiBackend.fetchDataPost<number>('Chat/Chat/LeaveConversation/' + conversationId);
        promise.then(() => {
            const conversations = this.fiChatSyncService.conversations;
            const currentConversationIdx = conversations.findIndex((item) => item.Id === conversationId);
            if (currentConversationIdx !== -1) {
                conversations.splice(currentConversationIdx, 1);
                this.fiChatSyncService.triggerConversationsUpdated({ updatedConversations: conversations });
            }
        });
        return this.fiLoading.trackPromise(promise);
    }

    changeConversationUsers(conversationId: number, users: ILightWeightUser[], actionType: ActionType) {
        const userIds = users.map((u) => u.Id);
        const promise = this.fiBackend.fetchDataPost<number>('Chat/Chat/ChangeConversationUsers/' + conversationId, {
            userIds: userIds,
            actionType: actionType,
        });
        promise.then(() => {
            // for proper updating conversation icon
            this.fiChatSyncService.updateConversation(conversationId, (conversation) => {
                users.forEach((user) => {
                    if (actionType === ActionType.Create) {
                        conversation.Users.push({
                            Id: user.Id,
                            FullName: user.FullName,
                            MediaBankPhysicalFileName: user.MediaBankPhysicalFileName,
                            MediaBankExtension: user.MediaBankExtension,
                            PreferredBusinessUnitName: user.PreferredBusinessUnitName,
                        });
                    } else if (actionType === ActionType.Delete) {
                        const currentUserIdx = conversation.Users.findIndex((u) => u.Id === user.Id);
                        if (currentUserIdx !== -1) {
                            conversation.Users.splice(currentUserIdx, 1);
                        }
                    }
                });
            });
        });
        return this.fiLoading.trackPromise(promise);
    }

    triggerIsConversationTypingChanged(conversationId: number, isTyping: boolean, userIds: number[]) {
        return this.fiBackend.fetchDataPost<void>(
            `Chat/Chat/TriggerIsConversationTypingChanged/${conversationId}/${isTyping}`,
            userIds,
        );
    }

    //region open conversation
    private _onOpenConversationById$: Subject<number> = new Subject<number>();
    get onOpenConversationById$(): Observable<number> {
        return this._onOpenConversationById$.asObservable();
    }

    triggerOpenOneToOneConversationByUserId(targetUserId: number) {
        if (this.mobileAppHelperService.frontEndMode === FrontendMode.WebsiteIFrame) {
            this.mobileAppHelperService.postMessage('fic.OpenOneToOneConversation', { userId: targetUserId });
        } else {
            if (!this.fiChatHelperService.isChatSupported()) return;

            this.openOneToOneConversationByUserId(targetUserId);
        }
    }

    triggerOpenConversationById(conversationId: number) {
        if (!this.fiChatHelperService.isChatSupported()) return;

        this.openConversationById(conversationId);
    }

    triggerOpenConversationByIdWithAccessCheck(conversationId: number) {
        const currentConversation = this.fiChatSyncService.findConversationById(conversationId);
        if (currentConversation) {
            this.triggerOpenConversationById(conversationId);
        } else {
            this.snackBar.showSnackbar({
                textMessage: this.appTextService.get('chat.not_member_a_group'),
                buttonText: this.appTextService.get('common.dismiss'),
            });
        }
    }

    private _openNewConversationByUser$: Subject<ILightWeightUserWithUnits> = new Subject<ILightWeightUserWithUnits>();
    get onOpenNewConversationByUser$(): Observable<ILightWeightUserWithUnits> {
        return this._openNewConversationByUser$.asObservable();
    }

    private openOneToOneConversationByUserId(targetUserId: number) {
        const conversation = this.fiChatHelperService.findOneToOneConversation([targetUserId]);
        if (isDefined(conversation)) {
            this._onOpenConversationById$.next(conversation.Id);
            return;
        }

        this.getAvailableUsersForConversation([targetUserId]).then((result: ILightWeightUserWithUnits[]) => {
            if (isNullOrUndefined(result) || !result.length) return;
            this._openNewConversationByUser$.next(result[0]);
        });
    }

    private openConversationById(conversationId: number) {
        if (!this.fiChatSyncService.conversationsCount) {
            this.fiChatSyncService.conversationsUpdated$
                .pipe(filter((v) => this.fiChatSyncService.conversationsCount > 0))
                .pipe(take(1))
                .subscribe(() => {
                    this._onOpenConversationById$.next(conversationId);
                });
        } else {
            this._onOpenConversationById$.next(conversationId);
        }
    }
    //endregion

    public isUserAvailableForConversation(userId: number): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.getAvailableUsersForConversation([userId]).then((result: ILightWeightUserWithUnits[]) => {
                resolve(!!result.length);
            }, reject);
        });
    }

    public triggerSuggestedConversationUpdate() {
        this._suggestedConversations$.next(this._suggestedConversations$.value);
    }
}
